/**
 * Service wrapper to push user story to GitHub issue and optional project.
 */
import { createIssue, ensureLabels, addIssueToProjectV2, deleteIssue } from './github';
import { StoryNode } from '../types';

export interface GitHubIssueResponse {
    html_url?: string;
    node_id?: string;
    message?: string;
}

export async function pushStoryToGitHub(
    story: StoryNode,
    githubToken: string | undefined,
    githubRepo: string,
    githubProjectId?: string
): Promise<GitHubIssueResponse> {
    if (!githubRepo) {
        throw new Error('GitHub repo required — check config');
    }

    const [owner, repo] = githubRepo.split('/');
    await ensureLabels(githubToken, owner, repo);

    const issue = await createIssue(githubToken, owner, repo, story);
    if (!issue.html_url) {
        throw new Error(issue.message || 'GitHub API error');
    }

    if (githubProjectId && issue.node_id) {
        try {
            await addIssueToProjectV2(githubToken, githubProjectId, issue.node_id);
        } catch (e) {
            console.warn('Could not add issue to GitHub Project:', (e as Error).message);
        }
    }

    return issue;
}

/**
 * Extracts the trailing issue number from a GitHub issue URL, or null when the
 * URL does not end in one (e.g. a link that was hand-edited).
 */
export function issueNumberFromUrl(githubUrl: string | undefined): string | null {
    if (!githubUrl) return null;
    const last = githubUrl.replace(/\/+$/, '').split('/').pop();
    if (!last || Number.isNaN(Number(last))) return null;
    return last;
}

/**
 * Closes the GitHub issue backing a story node.
 *
 * Throws when the integration is not configured or the URL carries no usable
 * issue number — the caller decides how loudly to fail, since deleting the node
 * itself normally proceeds regardless.
 */
export async function deleteStoryIssue(
    story: Pick<StoryNode, 'githubUrl'>,
    githubToken: string | undefined,
    githubRepo: string,
): Promise<void> {
    if (!githubToken || !githubRepo) throw new Error('GitHub token/repo not configured');

    const issueNumber = issueNumberFromUrl(story.githubUrl);
    if (!issueNumber) throw new Error('Invalid GitHub issue URL');

    const [owner, repo] = githubRepo.split('/');
    await deleteIssue(githubToken, owner, repo, issueNumber);
}
